import iOSParser from './iOSPushNotifications';
import androidParser from './androidPushNotifications';
import { Device } from 'services/Native/Device';
import { tryParse } from './PushNotifications';
import { getNavStackHandler, useNavStack } from 'careand-redux/slices/ui/hooks';
import { store } from 'careand-redux/store';
import { getUnwrappedDispatch } from 'careand-redux/useAppDispatch';
import { navigationActions, Stacks, Tabs } from 'careand-redux/slices/ui/navigation';
import { api } from 'careand-redux/api/ApiThunks';
import { U } from 'chart.js/dist/chunks/helpers.core';
import { deviceActions } from 'careand-redux/slices/device';
import { getAccountSwitchPromise } from 'scenes/HomePatient2/SwitchProfile/ConfirmationPopup';
import { AuthenticatedPatient } from 'careand-redux/selectors';
import { JitsiMeetingPayload } from 'generated/api';

let pendingPush: unknown | undefined;
let pendingUnsub: (() => void) | undefined;

function queuePushUntilLoggedIn(push: unknown) {
  // Latest tap wins — don't replay a stack of stale notifications when the
  // user finally signs in.
  pendingPush = push;
  if (pendingUnsub) return;
  pendingUnsub = store.subscribe(() => {
    const { loggedIn } = store.getState().authentication;
    if (!loggedIn) return;
    const queued = pendingPush;
    pendingPush = undefined;
    pendingUnsub?.();
    pendingUnsub = undefined;
    if (queued !== undefined) {
      handlePushNotification(queued);
    }
  });
}

export function clearPendingPushNotification() {
  pendingPush = undefined;
  pendingUnsub?.();
  pendingUnsub = undefined;
}

export async function handlePushNotification(push: unknown) {
  console.log('handling push:', push);
  if (!Device.isMobile()) {
    return;
  }
  const parser = getParser()!;
  const state = store.getState();
  const { loggedIn, activeUserId, user } = state.authentication;

  if (!loggedIn) {
    // Cold launch: redux-persist hasn't rehydrated `loggedIn` yet (or the
    // user isn't signed in). Hold the push and replay once auth resolves.
    queuePushUntilLoggedIn(push);
    return;
  }

  const dispatch = getUnwrappedDispatch(store.dispatch);
  const masterNav = getNavStackHandler(Stacks.Master, dispatch);

  /// Returns wether the account has been switch to the correct one
  async function switchToLinkedAccountIfNeeded(userId: string | undefined) {
    if (!userId || userId === activeUserId) {
      return true;
    }
    const activeIsMaster = user?.id === activeUserId;
    const linkedAccounts = (user?.id && state.patients.byId?.[user.id]?.linked_accounts) || [];
    const isLinkedAccount = linkedAccounts.find((it) => it.id === userId) !== undefined;

    const shouldSwitch =
      (activeIsMaster && isLinkedAccount) || (!activeIsMaster && userId === user?.id);

    if (shouldSwitch) {
      await new Promise((fulfill) => {
        fulfill(0);
      });
      dispatch(deviceActions.switchAccountConfirmationRequested(userId));
      return getAccountSwitchPromise(userId);
    }

    return true;
  }

  if (tryParse(parser.appointmentUpdated, push)) {
    const apptsNav = getNavStackHandler(Stacks.Appointments, dispatch);

    if (!(await switchToLinkedAccountIfNeeded(push.object.userId))) {
      return;
    }

    apptsNav.popToRoot();
    apptsNav.push(`/${push.object.id}`);
    dispatch(navigationActions.activeTabChanged({ tab: Tabs.Appointments }));
    masterNav.popTo('/');
  } else if (tryParse(parser.chatUpdated, push)) {
    if (!(await switchToLinkedAccountIfNeeded(push.object.userId))) {
      return;
    }
    masterNav.push(`/chat/${push.object.roomName}`);
  } else if (tryParse(parser.hrUpdated, push)) {
    if (!(await switchToLinkedAccountIfNeeded(push.object.userId))) {
      return;
    }

    const hrNav = getNavStackHandler(Stacks.HR, dispatch);
    // The `activeUserId` captured at line 23 may be stale after the
    // linked-account switch above — re-read from the live store and prefer
    // the notification's target userId so we refresh the right patient.
    const targetUserId =
      push.object.userId || store.getState().authentication.activeUserId!;

    let actualId = push.object.id;

    if (push.type === 'pending_document') {
      // Refresh CPP for the target patient so the healthRecords slice (used
      // by Detail / records list back-navigation) sees the freshly-issued
      // pending document. PatientTemplate self-fetches the doc independently.
      await dispatch(api.HealthRecords.getCPP({ patientId: targetUserId }));
      hrNav.popToRoot();
      masterNav.popTo('/');
      masterNav.push(`/patient-fill/${actualId}`);
      dispatch(navigationActions.activeTabChanged({ tab: Tabs.HR }));
      return;
    }

    const cpp = await dispatch(api.HealthRecords.getCPP({ patientId: targetUserId }));
    if (push.type === 'prescription') {
      actualId = cpp?.prescriptions?.[0]!.id ?? push.object.id;
    }

    const type = push.type + 's';
    hrNav.popToRoot();
    hrNav.push(`/records/${push.object.userId}/${type}/${actualId}`);
    dispatch(navigationActions.activeTabChanged({ tab: Tabs.HR }));
    masterNav.popTo('/');
  } else if (tryParse(parser.joinTelemedicine, push)) {
    //dont join directly, show the appt details page so that
    //we can ask for student consent
    const apptsNav = getNavStackHandler(Stacks.Appointments, dispatch);
    if (!(await switchToLinkedAccountIfNeeded(push.object.userId))) {
      return;
    }
    apptsNav.popToRoot();
    apptsNav.push(`/${push.object.id}`);
    dispatch(navigationActions.activeTabChanged({ tab: Tabs.Appointments }));
    masterNav.popTo('/');
  } else if (tryParse(parser.other, push)) {
    const hrNav = getNavStackHandler(Stacks.HR, dispatch);
    const menstruationNav = getNavStackHandler(Stacks.MenstruationTracker, dispatch);
    switch (push.type) {
      case 'health_records':
        dispatch(navigationActions.activeTabChanged({ tab: Tabs.HR }));
        masterNav.popTo('/');
        break;
      case 'menstruation':
        menstruationNav.popToRoot();
        dispatch(navigationActions.activeTabChanged({ tab: Tabs.MenstruationTracker }));
        break;
      case 'subscription':
        masterNav.popTo('/');
        masterNav.push('/billing');
        break;
      case 'version_update':
        break;
      default:
        break;
    }
  } else if (tryParse(parser.medicationReminder, push)) {
    const userId = push.object.userId;
    const activeUserId = store.getState().authentication.activeUserId;
    const authenticatedUser = AuthenticatedPatient(store.getState());
    const isFromFollowingLinked = (
      authenticatedUser?.medication_tracker_settings?.follow_linked_accounts ?? []
    )?.includes(push.object.userId!);
    const isActiveMaster = activeUserId === authenticatedUser?.id;

    const shouldNotSwitch = userId === activeUserId || (isActiveMaster && isFromFollowingLinked);

    if (!shouldNotSwitch) {
      await new Promise((fulfill) => {
        fulfill(0);
      });
      dispatch(deviceActions.switchAccountConfirmationRequested(userId));
      const switched = await getAccountSwitchPromise(userId!);
      if (!switched) {
        return;
      }
    }

    dispatch(navigationActions.activeTabChanged({ tab: Tabs.Home }));
    masterNav.popTo('/');
  } else {
    //is none
  }
}

function getParser() {
  if (Device.isAndroid()) {
    return androidParser;
  }
  if (Device.isIOS()) {
    return iOSParser;
  }
}
